import { useEffect, useState } from "react";
import { Save, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { getSettings, updateSettings } from "@/lib/adminCmsApi";
import AdminShell from "@/components/admin/AdminShell";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettings().then((d) => setS({
      site_name: "AGS Services", tagline: "", logo_url: "",
      primary_color: "#0E2A57", accent_color: "#2BB6E5",
      contact_email: "", contact_phone: "", address: "",
      facebook_url: "", linkedin_url: "", twitter_url: "", instagram_url: "",
      footer_tagline: "", nav_items: [], compliance_badges: [],
      ...d,
    })).catch(() => toast.error("Load failed")).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await updateSettings(s); toast.success("Settings saved"); }
    catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (loading || !s) return <AdminShell title="Settings"><div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin" /></div></AdminShell>;

  const set = (k, v) => setS({ ...s, [k]: v });

  return (
    <AdminShell title="Settings">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-ags-text-muted">Brand, contact info, navigation, footer. Changes apply across the site.</p>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card title="Brand">
          <F label="Site name"><input className="inp" value={s.site_name} onChange={(e) => set("site_name", e.target.value)} /></F>
          <F label="Tagline"><input className="inp" value={s.tagline} onChange={(e) => set("tagline", e.target.value)} /></F>
          <F label="Logo URL"><input className="inp" value={s.logo_url} onChange={(e) => set("logo_url", e.target.value)} /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Primary colour"><input type="color" className="h-10 w-full border border-[#0E2A57]/15" value={s.primary_color} onChange={(e) => set("primary_color", e.target.value)} /></F>
            <F label="Accent colour"><input type="color" className="h-10 w-full border border-[#0E2A57]/15" value={s.accent_color} onChange={(e) => set("accent_color", e.target.value)} /></F>
          </div>
        </Card>

        <Card title="Contact">
          <F label="Email"><input className="inp" value={s.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></F>
          <F label="Phone"><input className="inp" value={s.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></F>
          <F label="Address"><input className="inp" value={s.address} onChange={(e) => set("address", e.target.value)} /></F>
        </Card>

        <Card title="Social">
          <F label="Facebook"><input className="inp" value={s.facebook_url} onChange={(e) => set("facebook_url", e.target.value)} /></F>
          <F label="LinkedIn"><input className="inp" value={s.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} /></F>
          <F label="Twitter / X"><input className="inp" value={s.twitter_url} onChange={(e) => set("twitter_url", e.target.value)} /></F>
          <F label="Instagram"><input className="inp" value={s.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} /></F>
        </Card>

        <Card title="Footer">
          <F label="Footer tagline (italic quote)"><textarea rows={2} className="inp" value={s.footer_tagline} onChange={(e) => set("footer_tagline", e.target.value)} /></F>
          <ArrayString label="Compliance badges" items={s.compliance_badges || []} onChange={(v) => set("compliance_badges", v)} />
        </Card>

        <Card title="Navigation" wide>
          <NavEditor items={s.nav_items || []} onChange={(v) => set("nav_items", v)} />
        </Card>
      </div>
      <style>{`.inp{width:100%;background:white;border:1px solid rgba(14,42,87,0.15);padding:8px 12px;outline:none}.inp:focus{border-color:#0E2A57}`}</style>
    </AdminShell>
  );
}

function Card({ title, children, wide }) {
  return (
    <div className={`bg-white border border-[#0E2A57]/15 p-6 ${wide ? "md:col-span-2" : ""}`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55 mb-4">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function F({ label, children }) {
  return <label className="block"><span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-1.5">{label}</span>{children}</label>;
}

function ArrayString({ label, items, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted">{label}</span>
        <button type="button" onClick={() => onChange([...items, ""])} className="inline-flex items-center gap-1 bg-[#0E2A57] text-white px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em]"><Plus size={10} /> Add</button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input className="inp text-sm" value={it} onChange={(e) => { const n=[...items]; n[i]=e.target.value; onChange(n); }} />
            <button type="button" onClick={() => { const n=[...items]; n.splice(i,1); onChange(n); }} className="px-2 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function NavEditor({ items, onChange }) {
  const add = () => onChange([...items, { label: "", href: "/", order: items.length+1, children: [] }]);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted">Top navigation</span>
        <button type="button" onClick={add} className="inline-flex items-center gap-1 bg-[#0E2A57] text-white px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em]"><Plus size={10} /> Add link</button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <input className="inp text-sm col-span-4" placeholder="Label" value={it.label} onChange={(e) => { const n=[...items]; n[i]={...n[i], label:e.target.value}; onChange(n); }} />
            <input className="inp text-sm col-span-6" placeholder="/path" value={it.href} onChange={(e) => { const n=[...items]; n[i]={...n[i], href:e.target.value}; onChange(n); }} />
            <input className="inp text-sm col-span-1" type="number" value={it.order} onChange={(e) => { const n=[...items]; n[i]={...n[i], order:parseInt(e.target.value)||0}; onChange(n); }} />
            <button type="button" onClick={() => { const n=[...items]; n.splice(i,1); onChange(n); }} className="col-span-1 text-red-600 hover:bg-red-50 p-2"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
